import { dialCodeFor } from './countries';

export interface DetectCountryOptions {
  // Preferred locale tags, most-preferred first (defaults to the browser locales).
  locales?: readonly string[];
  // IANA time zone (defaults to the browser time zone).
  timeZone?: string;
  // Used when neither locale nor time zone resolves to a known country.
  fallback?: string;
}

// Common IANA time zones -> ISO2, used only when the locale carries no region.
const TIME_ZONE_COUNTRY: Record<string, string> = {
  'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN', 'Asia/Karachi': 'PK', 'Asia/Dhaka': 'BD',
  'Asia/Colombo': 'LK', 'Asia/Kathmandu': 'NP', 'Asia/Dubai': 'AE', 'Asia/Riyadh': 'SA',
  'Asia/Tehran': 'IR', 'Asia/Jerusalem': 'IL', 'Asia/Shanghai': 'CN', 'Asia/Hong_Kong': 'HK',
  'Asia/Singapore': 'SG', 'Asia/Tokyo': 'JP', 'Asia/Seoul': 'KR', 'Asia/Bangkok': 'TH',
  'Asia/Jakarta': 'ID', 'Asia/Manila': 'PH', 'Asia/Kuala_Lumpur': 'MY',
  'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US', 'America/Phoenix': 'US',
  'America/Los_Angeles': 'US', 'America/Anchorage': 'US', 'Pacific/Honolulu': 'US',
  'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Sao_Paulo': 'BR',
  'America/Mexico_City': 'MX', 'America/Bogota': 'CO', 'America/Lima': 'PE', 'America/Santiago': 'CL',
  'America/Argentina/Buenos_Aires': 'AR',
  'Europe/London': 'GB', 'Europe/Dublin': 'IE', 'Europe/Paris': 'FR', 'Europe/Berlin': 'DE',
  'Europe/Madrid': 'ES', 'Europe/Rome': 'IT', 'Europe/Amsterdam': 'NL', 'Europe/Brussels': 'BE',
  'Europe/Zurich': 'CH', 'Europe/Vienna': 'AT', 'Europe/Stockholm': 'SE', 'Europe/Oslo': 'NO',
  'Europe/Copenhagen': 'DK', 'Europe/Helsinki': 'FI', 'Europe/Warsaw': 'PL', 'Europe/Lisbon': 'PT',
  'Europe/Moscow': 'RU', 'Europe/Istanbul': 'TR', 'Europe/Athens': 'GR',
  'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Perth': 'AU', 'Pacific/Auckland': 'NZ',
  'Africa/Cairo': 'EG', 'Africa/Johannesburg': 'ZA', 'Africa/Lagos': 'NG', 'Africa/Nairobi': 'KE',
};

function regionFromLocale(locale: string): string | undefined {
  try {
    const region = new Intl.Locale(locale).region;
    return region ? region.toUpperCase() : undefined;
  } catch {
    const match = /[-_]([A-Za-z]{2})(?:$|[-_])/.exec(locale);
    return match ? match[1].toUpperCase() : undefined;
  }
}

function browserLocales(): readonly string[] {
  if (typeof navigator === 'undefined') {
    return [];
  }
  if (navigator.languages && navigator.languages.length > 0) {
    return navigator.languages;
  }
  return navigator.language ? [navigator.language] : [];
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    return '';
  }
}

// Best-effort default country (ISO2) from the user's time zone, then locale, then a fallback.
//
// Time zone is checked FIRST because it reflects where the user physically is, whereas the
// browser locale encodes the language's country (e.g. `en-US` on a device used in India) —
// a poor signal for a phone dialing code. The locale region is only a secondary hint used
// when the time zone maps to no known country.
export function detectDefaultCountry(options: DetectCountryOptions = {}): string {
  const fallback = options.fallback ?? 'IN';

  const timeZone = options.timeZone ?? browserTimeZone();
  const fromTimeZone = TIME_ZONE_COUNTRY[timeZone];
  if (fromTimeZone && dialCodeFor(fromTimeZone)) {
    return fromTimeZone;
  }

  const locales = options.locales ?? browserLocales();
  for (const locale of locales) {
    const region = regionFromLocale(locale);
    if (region && dialCodeFor(region)) {
      return region;
    }
  }

  return fallback;
}
